<template>
  <div @click="handle_card_click">
    <v-card
      :class="[$style.orders_widget, { [$style.pointerDisabled]: disable_card_click }]"
      :loading="credit_line_loading"
    >
      <div :class="$style.widget_title">
        <span class="display-3">Сумма задолженности перед АТОЛ</span>
      </div>

      <template v-if="is_filters">
        <div class="d-flex mb-6">
          <v-row>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <div @click.stop>
                <Select
                  :value="filters.credit_line_organization_atols"
                  :items="external_orgs"
                  item-text="name_organization"
                  item_value="id"
                  label="Организация АТОЛ"
                  dense
                  hide-details
                  clearable
                  blue_icon
                  multiple
                  @input="filter_update('credit_line_organization_atols', $event, 'credit_line')"
                />
              </div>
            </v-col>

            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <div @click.stop>
                <Select
                  :value="filters.credit_line_organization_ids"
                  :items="organizations"
                  item-text="name_organization"
                  item_value="id"
                  label="Организация"
                  dense
                  hide-details
                  clearable
                  blue_icon
                  multiple
                  @input="filter_update('credit_line_organization_ids', $event, 'credit_line')"
                />
              </div>
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                v-if="filters.credit_line_organization_ids.length > 0"
                :value="filters.credit_line_contract_ids"
                :items="credit_line_contracts"
                label="Договор"
                item-text="name"
                item_value="id"
                multiple
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('credit_line_contract_ids', $event, 'credit_line')"
              />
            </v-col>
          </v-row>
        </div>
      </template>
      <template v-if="is_filters">
        <div class="d-flex align-center justify-space-between pl-4 pr-4">
          <span :class="$style.balanceText">Баланс:</span>
          <span
            class="font-weight-bold"
            :class="[ $style[currency_color(analytics_credit_line.balance)], $style.balanceCountText ]"
          >
            {{ analytics_credit_line.balance | currency_rub }}
            <v-tooltip
              bottom
              color="primary darken-3"
              :open-delay="300"
              :content-class="$style.commentTooltip"
            >
              <template
                v-slot:activator="{ on, attrs }"
              >
                <v-icon
                  class="ml-1"
                  :class="$style.icon"
                  v-bind="attrs"
                  v-on="on"
                  size="18px"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>
                Положительное значение баланса - переплаты партнера.
                Отрицательное значение баланса - задолженность партнера перед АТОЛ
              </span>
            </v-tooltip>
          </span>
        </div>
        <div
          :class="$style.widget_box"
          class="mt-5"
        >
          <div class="d-flex align-center pl-4 pr-4">
            <span class="font-weight-medium">Задолженность:</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Сумма отгрузки</span>
            <span
              class="headline font-weight-bold"
            >{{ analytics_credit_line.shipment_amount | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Общая задолженность</span>
            <span
              class="headline font-weight-bold"
            >{{ analytics_credit_line.total_debt | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Просроченная задолженность</span>
            <span
              class="headline font-weight-bold"
            >{{ analytics_credit_line.overdue_debt | currency_rub }}</span>
          </div>
        </div>
        <div
          :class="$style.widget_box"
          class="mt-5"
        >
          <div
            class="d-flex flex-wrap align-center pl-4 pr-4"
            :class="$style.gap"
          >
            <span class="font-weight-medium">График оплаты:</span>
            <v-chip
              v-if="isShippingStopped"
              color="error"
              small
            >
              Отгрузка приостановлена
            </v-chip>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Просрочено</span>
            <span
              class="headline font-weight-bold"
            >{{ analytics_credit_line.overdue_debt | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">К оплате через 3 дня</span>
            <span
              class="headline font-weight-bold"
            >{{ analytics_credit_line.to_pay_three_days | currency_rub }}</span>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="d-flex align-center justify-space-between pl-4 pr-4">
          <span :class="$style.balanceText">Баланс:</span>
          <span
            class="font-weight-bold"
            :class="[ $style[currency_color(credit_line.balance)], $style.balanceCountText ]"
          >
            {{ credit_line.balance | currency_rub }}
            <v-tooltip
              bottom
              color="primary darken-3"
              :open-delay="300"
              :content-class="$style.commentTooltip"
            >
              <template
                v-slot:activator="{ on, attrs }"
              >
                <v-icon
                  class="ml-1"
                  :class="$style.icon"
                  v-bind="attrs"
                  v-on="on"
                  size="18px"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>
                Положительное значение баланса - переплаты партнера.
                Отрицательное значение баланса - задолженность партнера перед АТОЛ
              </span>
            </v-tooltip>
          </span>
        </div>
        <div
          :class="$style.widget_box"
          class="mt-5"
        >
          <div class="d-flex align-center pl-4 pr-4">
            <span class="font-weight-medium">Задолженность:</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Сумма отгрузки</span>
            <span
              class="headline font-weight-bold"
            >{{ credit_line.shipment_amount | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Общая задолженность</span>
            <span
              class="headline font-weight-bold"
            >{{ credit_line.total_debt | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Просроченная задолженность</span>
            <span
              class="headline font-weight-bold"
            >{{ credit_line.overdue_debt | currency_rub }}</span>
          </div>
        </div>
        <div
          :class="$style.widget_box"
          class="mt-5"
        >
          <div
            class="d-flex flex-wrap align-center pl-4 pr-4"
            :class="$style.gap"
          >
            <span class="font-weight-medium">График оплаты:</span>
            <v-chip
              v-if="isShippingStopped"
              color="error"
              small
            >
              Отгрузка приостановлена
            </v-chip>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">Просрочено</span>
            <span
              class="headline font-weight-bold"
            >{{ credit_line.overdue_debt | currency_rub }}</span>
          </div>
          <div :class="$style.widget_item">
            <span class="headline font-weight-regular">К оплате через 3 дня</span>
            <span
              class="headline font-weight-bold"
            >{{ credit_line.to_pay_three_days | currency_rub }}</span>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Emit,
} from 'vue-property-decorator';
import { Select } from '@/components/global';
import { Reports } from '@/modules/reports/types';
import { widgets_store, analytics_store } from '@/store';
import { AnalyticsFilters } from '@/modules/analytics/types';

const { state } = widgets_store;

const analytics_state = analytics_store.state;

@Component({
  components: {
    Select,
  },
})
export default class WidgetSettlement extends Vue {
  @Prop({ type: Boolean, default: null })
  isShippingStopped!: boolean | null

  @Prop({ type: Boolean, default: false })
  is_filters!: boolean;

  @Prop({ type: Boolean, default: false })
  disable_card_click!: boolean;

  @Emit()
  filter_update(key: keyof AnalyticsFilters, value: unknown, section: string) {
    if (key === 'credit_line_organization_ids' && Array.isArray(value) && value.length === 0) {
      analytics_store.mutations.UPD_FILTER_KEY({ key: 'credit_line_contract_ids', value: [], section });
    }

    analytics_store.mutations.UPD_FILTER_KEY({ key, value, section });
  }

  get credit_line() {
    return state.widgets?.credit_line;
  }

  get credit_line_loading() {
    return analytics_state.credit_line_loading;
  }

  get analytics_credit_line() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.credit_line
      : analytics_state.analytics.credit_line;
  }

  get filters() {
    return analytics_state.filters;
  }

  get organizations() {
    return analytics_state.organizations || [];
  }

  get external_orgs() {
    return analytics_state.external_organization;
  }

  get credit_line_contracts() {
    return analytics_state.credit_line_contracts || [];
  }

  has_response_filters() {
    return (
      analytics_state.filters.credit_line_organization_ids.length > 0
    );
  }

  currency_color(value: number): string {
    if (value > 0) return 'currencyColorGreen';
    if (value < 0) return 'currencyColorRed';
    return '';
  }

  handle_card_click() {
    if (this.disable_card_click) return;
    this.go_to_mutual();
  }

  go_to_mutual() {
    this.$router.push({
      name: Reports.Pages.REPORTS_MUTUAL_DETAIL,
      params: {
        from: 'widget',
      },
    });
  }
}
</script>

<style module lang="scss">
@import '@/scss/common/filter-blocks.scss';

.currencyColorRed {
  color: #EB5757;
}

.currencyColorGreen {
  color: #27AE60;
}

.orders_widget {
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.orders_widget:hover {
  cursor: pointer;
}

.widget_box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget_title {
  margin-bottom: 24px;
  font-style: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #13141C;
}

.widget_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
}

.widget_item:nth-child(even) {
  background-color: #F6F7FA;
  border-radius: 8px;
}

.balanceText {
  font-weight: 600;
  line-height: 20px;
  color: #13141C;
  font-size: 18px;
}

.balanceCountText {
  line-height: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.commentTooltip {
  max-width: 400px;
}

.gap {
  column-gap: 8px;
}

.pointerDisabled {
  cursor: default !important;
}
</style>
